import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const WorkspaceContext = createContext();

export const WorkspaceProvider = ({ children }) => {
  const { user } = useAuth();

  const [workspaces, setWorkspaces] = useState(() => {
    const saved = localStorage.getItem('forma_workspaces');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {}
    }
    return [];
  });

  const [activeWorkspaceId, setActiveWorkspaceId] = useState(() => {
    return localStorage.getItem('forma_active_ws') || '';
  });

  const [members, setMembers] = useState(() => {
    const saved = localStorage.getItem('forma_members');
    return saved ? JSON.parse(saved) : [];
  });

  const [pendingInvites, setPendingInvites] = useState(() => {
    const saved = localStorage.getItem('forma_invites');
    return saved ? JSON.parse(saved) : [];
  });

  const [teams, setTeams] = useState(() => {
    const saved = localStorage.getItem('forma_teams');
    return saved ? JSON.parse(saved) : [];
  });

  const [projects, setProjects] = useState(() => {
    const saved = localStorage.getItem('forma_projects');
    return saved ? JSON.parse(saved) : [];
  });

  const [activeProjectId, setActiveProjectId] = useState(() => {
    return localStorage.getItem('forma_active_prj') || '';
  });

  const [tasks, setTasks] = useState(() => {
    const saved = localStorage.getItem('forma_tasks');
    return saved ? JSON.parse(saved) : [];
  });

  const [sprints, setSprints] = useState(() => {
    const saved = localStorage.getItem('forma_sprints');
    return saved ? JSON.parse(saved) : [];
  });

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem('forma_sidebar_collapsed') === 'true';
  });

  const [selectedTask, setSelectedTask] = useState(null);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);

  // Sync with LocalStorage
  useEffect(() => {
    localStorage.setItem('forma_workspaces', JSON.stringify(workspaces));
  }, [workspaces]);

  useEffect(() => {
    if (activeWorkspaceId) {
      localStorage.setItem('forma_active_ws', activeWorkspaceId);
    } else {
      localStorage.removeItem('forma_active_ws');
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    localStorage.setItem('forma_members', JSON.stringify(members));
  }, [members]);

  useEffect(() => {
    localStorage.setItem('forma_invites', JSON.stringify(pendingInvites));
  }, [pendingInvites]);

  useEffect(() => {
    localStorage.setItem('forma_teams', JSON.stringify(teams));
  }, [teams]);

  useEffect(() => {
    localStorage.setItem('forma_projects', JSON.stringify(projects));
  }, [projects]);

  useEffect(() => {
    if (activeProjectId) {
      localStorage.setItem('forma_active_prj', activeProjectId);
    } else {
      localStorage.removeItem('forma_active_prj');
    }
  }, [activeProjectId]);

  useEffect(() => {
    localStorage.setItem('forma_tasks', JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    localStorage.setItem('forma_sprints', JSON.stringify(sprints));
  }, [sprints]);

  useEffect(() => {
    localStorage.setItem('forma_sidebar_collapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Active Workspace resolution
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) || workspaces[0] || null;

  // Active Workspace ID sync if out of sync
  useEffect(() => {
    if (workspaces.length > 0 && (!activeWorkspaceId || !workspaces.some((w) => w.id === activeWorkspaceId))) {
      setActiveWorkspaceId(workspaces[0].id);
    }
  }, [workspaces, activeWorkspaceId]);

  // Strict Workspace Project Scoping: Only return projects belonging to the active workspace
  const currentWorkspaceProjects = activeWorkspace
    ? projects.filter((p) => p.workspaceId === activeWorkspace.id)
    : [];

  // Active Project resolution within active workspace
  const activeProject =
    currentWorkspaceProjects.find((p) => p.id === activeProjectId) ||
    currentWorkspaceProjects[0] ||
    null;

  // Sync activeProjectId when switching workspaces
  useEffect(() => {
    if (activeWorkspace) {
      const wsProjects = projects.filter((p) => p.workspaceId === activeWorkspace.id);
      if (wsProjects.length > 0) {
        const isCurrentPrjInWs = wsProjects.some((p) => p.id === activeProjectId);
        if (!isCurrentPrjInWs) {
          setActiveProjectId(wsProjects[0].id);
        }
      } else {
        setActiveProjectId('');
      }
    }
  }, [activeWorkspaceId, projects, activeProjectId, activeWorkspace]);

  // Tasks & Sprints strictly scoped to activeProject
  const currentProjectTasks = activeProject ? tasks.filter((t) => t.projectId === activeProject.id) : [];
  const currentProjectSprints = activeProject ? sprints.filter((s) => s.projectId === activeProject.id) : [];
  const activeSprint = currentProjectSprints.find((s) => s.status === 'active') || null;

  // Members, Invites, Teams strictly scoped to activeWorkspace
  const currentWorkspaceMembers = activeWorkspace ? members.filter((m) => m.workspaceId === activeWorkspace.id) : [];
  const currentWorkspaceInvites = activeWorkspace ? pendingInvites.filter((i) => i.workspaceId === activeWorkspace.id) : [];
  const currentWorkspaceTeams = activeWorkspace ? teams.filter((t) => t.workspaceId === activeWorkspace.id) : [];

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  // Create Workspace (Creates ONLY the workspace, no duplicate rogue projects)
  const createWorkspace = ({ name, slug, icon }) => {
    const wsOwnerId = user?.id || 'usr_' + Date.now();
    const wsOwnerName = user?.name || 'Workspace Owner';
    const wsOwnerEmail = user?.email || 'owner@forma.build';
    const wsOwnerAvatar = user?.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(wsOwnerName)}`;

    const newWsId = 'ws_' + Date.now();
    const newWs = {
      id: newWsId,
      name,
      slug: slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      icon: icon || 'deployed_code',
      ownerId: wsOwnerId,
      membersCount: 1,
      plan: 'Pro Free'
    };

    setWorkspaces((prev) => [...prev, newWs]);
    setActiveWorkspaceId(newWs.id);

    const newMembership = {
      membershipId: 'mem_' + Date.now(),
      workspaceId: newWs.id,
      user: {
        id: wsOwnerId,
        name: wsOwnerName,
        email: wsOwnerEmail,
        avatar: wsOwnerAvatar
      },
      role: 'owner',
      status: 'active',
      joinedAt: new Date().toISOString().split('T')[0]
    };
    setMembers((prev) => [...prev, newMembership]);

    return newWs;
  };

  const updateWorkspace = (workspaceId, updates) => {
    setWorkspaces((prev) =>
      prev.map((w) => (w.id === workspaceId ? { ...w, ...updates } : w))
    );
  };

  const inviteMember = (email, role = 'member') => {
    if (!activeWorkspace) return null;
    const newInvite = {
      id: 'inv_' + Date.now(),
      workspaceId: activeWorkspace.id,
      email,
      role,
      status: 'pending',
      invitedAt: new Date().toISOString().split('T')[0]
    };
    setPendingInvites((prev) => [...prev, newInvite]);
    return newInvite;
  };

  const updateMemberRole = (userId, newRole) => {
    if (!activeWorkspace) return;
    setMembers((prev) =>
      prev.map((m) =>
        m.workspaceId === activeWorkspace.id && m.user.id === userId ? { ...m, role: newRole } : m
      )
    );
  };

  const removeMember = (userId) => {
    if (!activeWorkspace) return;
    setMembers((prev) =>
      prev.filter((m) => !(m.workspaceId === activeWorkspace.id && m.user.id === userId))
    );
  };

  const revokeInvite = (inviteId) => {
    setPendingInvites((prev) => prev.filter((i) => i.id !== inviteId));
  };

  const createTeam = (teamName, teamIcon = 'group') => {
    if (!activeWorkspace) return null;
    const newTeam = {
      id: 'tm_' + Date.now(),
      workspaceId: activeWorkspace.id,
      name: teamName,
      icon: teamIcon,
      membersCount: 1
    };
    setTeams((prev) => [...prev, newTeam]);
    return newTeam;
  };

  // Create Project strictly in specified workspace (or currently active workspace)
  const createProject = (projectData) => {
    const targetWsId = projectData.workspaceId || activeWorkspace?.id || activeWorkspaceId;
    const prjLead = projectData.lead || user?.name || 'Workspace Owner';
    const newPrj = {
      id: 'prj_' + Date.now(),
      workspaceId: targetWsId,
      status: 'Active',
      visibility: 'Public',
      progress: 0,
      lead: prjLead,
      updatedAt: new Date().toISOString().split('T')[0],
      ...projectData
    };

    setProjects((prev) => [newPrj, ...prev]);
    setActiveProjectId(newPrj.id);
    return newPrj;
  };

  const updateProject = (projectId, updates) => {
    setProjects((prev) =>
      prev.map((p) => (p.id === projectId ? { ...p, ...updates, updatedAt: new Date().toISOString().split('T')[0] } : p))
    );
  };

  const archiveProject = (projectId) => {
    updateProject(projectId, { status: 'Archived' });
  };

  const restoreProject = (projectId) => {
    updateProject(projectId, { status: 'Active' });
  };

  const deleteProject = (projectId) => {
    setProjects((prev) => prev.filter((p) => p.id !== projectId));
    setTasks((prev) => prev.filter((t) => t.projectId !== projectId));
  };

  const addTask = (taskData) => {
    const targetPrjId = taskData.projectId || activeProjectId;
    const prj = projects.find((p) => p.id === targetPrjId) || activeProject;
    const key = prj ? prj.key : 'FOR';
    const currentAssignee = taskData.assignee || (user ? { name: user.name, avatar: user.avatar } : { name: 'Unassigned', avatar: '' });

    const newTask = {
      id: `${key}-${Math.floor(100 + Math.random() * 900)}`,
      projectId: targetPrjId,
      sprintId: activeSprint ? activeSprint.id : null,
      status: 'todo',
      priority: 'medium',
      dueDate: new Date(Date.now() + 86400000 * 3).toISOString().split('T')[0],
      labels: [],
      checklists: [],
      comments: [],
      subtasks: [],
      assignee: currentAssignee,
      ...taskData
    };
    setTasks((prev) => [newTask, ...prev]);
    return newTask;
  };

  const updateTask = (taskId, updates) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t))
    );
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask((prev) => ({ ...prev, ...updates }));
    }
  };

  const deleteTask = (taskId) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(null);
    }
  };

  const moveTaskStatus = (taskId, newStatus) => {
    updateTask(taskId, { status: newStatus });
  };

  const moveTaskToSprint = (taskId, sprintId) => {
    updateTask(taskId, {
      sprintId: sprintId,
      status: sprintId && tasks.find((t) => t.id === taskId)?.status === 'backlog' ? 'todo' : undefined
    });
  };

  const startSprint = ({ name, duration, goal, startDate, endDate, taskIds = [] }) => {
    if (!activeProject) return null;
    const newSprintId = 'sprint_' + Date.now();
    const newSprint = {
      id: newSprintId,
      projectId: activeProject.id,
      name: name || `Sprint ${currentProjectSprints.length + 1}`,
      status: 'active',
      duration: duration || '2 weeks',
      goal: goal || 'Execute committed milestone deliverables.',
      startDate: startDate || new Date().toISOString().split('T')[0],
      endDate: endDate || new Date(Date.now() + 86400000 * 14).toISOString().split('T')[0]
    };

    setSprints((prev) => [
      ...prev.map((s) => (s.projectId === activeProject.id && s.status === 'active' ? { ...s, status: 'completed' } : s)),
      newSprint
    ]);

    if (taskIds.length > 0) {
      setTasks((prev) =>
        prev.map((t) => {
          if (taskIds.includes(t.id)) {
            return {
              ...t,
              sprintId: newSprintId,
              status: t.status === 'backlog' ? 'todo' : t.status
            };
          }
          return t;
        })
      );
    }

    return newSprint;
  };

  const completeSprint = (sprintId) => {
    setSprints((prev) =>
      prev.map((s) => (s.id === sprintId ? { ...s, status: 'completed' } : s))
    );
    setTasks((prev) =>
      prev.map((t) => {
        if (t.sprintId === sprintId && t.status !== 'done') {
          return { ...t, sprintId: null, status: 'backlog' };
        }
        return t;
      })
    );
  };

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        activeWorkspace,
        setActiveWorkspaceId,
        createWorkspace,
        updateWorkspace,
        members: currentWorkspaceMembers,
        pendingInvites: currentWorkspaceInvites,
        teams: currentWorkspaceTeams,
        inviteMember,
        updateMemberRole,
        removeMember,
        revokeInvite,
        createTeam,
        projects: currentWorkspaceProjects,
        allProjects: projects,
        activeProject,
        setActiveProjectId,
        createProject,
        updateProject,
        archiveProject,
        restoreProject,
        deleteProject,
        tasks: currentProjectTasks,
        allTasks: tasks,
        sprints: currentProjectSprints,
        allSprints: sprints,
        activeSprint,
        startSprint,
        completeSprint,
        moveTaskToSprint,
        selectedTask,
        setSelectedTask,
        addTask,
        updateTask,
        deleteTask,
        moveTaskStatus,
        isQuickAddOpen,
        setIsQuickAddOpen,
        isCommandPaletteOpen,
        setIsCommandPaletteOpen,
        isCreateWorkspaceModalOpen,
        setIsCreateWorkspaceModalOpen,
        isInviteModalOpen,
        setIsInviteModalOpen,
        isCreateProjectModalOpen,
        setIsCreateProjectModalOpen,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        toggleSidebar
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => useContext(WorkspaceContext);
